import { formatNumber } from '@/utils/number'
import type { CollectionInfoDTO, DistributeFeedItem } from '@/api/distribute'

export { formatNumber }

export function formatCount(value: number | null | undefined): string {
  return formatNumber(value ?? 0)
}

export function stripMarkup(value: string | null | undefined): string {
  if (!value) {
    return ''
  }

  return value
    .replace(/\{\{\s*([^{}]*?::[^{}]*?::[^{}]*?)\s*\}\}/g, (_match, token: string) => {
      const [, , ...displayParts] = token.split('::')
      const displayText = displayParts.join('::').trim()

      return displayText || _match
    })
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function isPositiveCount(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function formatCollectionEpisode(collectionInfo: CollectionInfoDTO): string {
  const episodeParts: string[] = []

  if (isPositiveCount(collectionInfo.sn)) {
    episodeParts.push(`第${collectionInfo.sn}集`)
  }

  if (isPositiveCount(collectionInfo.totalItems)) {
    episodeParts.push(`总${collectionInfo.totalItems}集`)
  }

  return episodeParts.join('/')
}

function joinTitleAndSummary(title: string, summary: string, divider: ' · ' | '｜'): string {
  if (title && summary && title !== summary) {
    return `${title}${divider}${summary}`
  }

  return title || summary
}

export function formatFeedCardTitle(
  item: DistributeFeedItem,
  fallbackTitle = 'Dreama 内容',
): string {
  const summaryText = stripMarkup(item.summaryMarkup || item.summary)
  const collectionInfo = item.collectionInfo

  if (collectionInfo?.collectionId || collectionInfo?.name) {
    const collectionName = stripMarkup(collectionInfo.name || collectionInfo.displayName)
    const episode = formatCollectionEpisode(collectionInfo)
    const collectionTitle = [collectionName, episode].filter(Boolean).join(' · ')

    return joinTitleAndSummary(collectionTitle, summaryText, '｜') || fallbackTitle
  }

  if (item.obj === 'radio' || item.obj === 'pipe') {
    const titleText = stripMarkup(item.name)

    return joinTitleAndSummary(titleText, summaryText, ' · ') || fallbackTitle
  }

  return stripMarkup(item.summaryMarkup || item.summary || item.name) || fallbackTitle
}

export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '刚刚更新'
  }

  const parsed = new Date(value.replace(/-/g, '/'))

  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed)
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) {
    return '00:00'
  }

  const totalSeconds = Math.floor(seconds)
  const minutes = Math.floor(totalSeconds / 60)
  const remainSeconds = totalSeconds % 60

  return `${String(minutes).padStart(2, '0')}:${String(remainSeconds).padStart(2, '0')}`
}
